import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { createPortal } from "react-dom";
import { $getRoot, $getNearestNodeFromDOMNode } from "lexical";
import { $isTableCellNode, $isTableRowNode, $isTableNode } from "@lexical/table";

//THIS IS 100% CURSOR
const TableResizerContainer = (props) => {
    const { anchorElem } = props;
    const [editor] = useLexicalComposerContext();
    const [resizeHandles, setResizeHandles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const dragState = useRef({
        startX: 0,
        columnIndex: 0,
        tableElement: null,
        startWidths: [],
    });

    // Find all tables and their column positions
    const updateResizeHandles = useCallback(() => {
        const rootElement = editor.getRootElement();
        if (!rootElement) return;

        const tables = rootElement.querySelectorAll('table');
        const handles = [];

        tables.forEach((table, tableIndex) => {
            const firstRow = table.querySelector('tr');
            if (!firstRow) return;

            const cells = firstRow.querySelectorAll('th, td');
            const tableRect = table.getBoundingClientRect();
            const anchorRect = anchorElem.getBoundingClientRect();

            cells.forEach((cell, cellIndex) => {
                // Don't add handle after the last column
                if (cellIndex === cells.length - 1) return;

                const cellRect = cell.getBoundingClientRect();

                handles.push({
                    id: `${tableIndex}-${cellIndex}`,
                    left: cellRect.right - anchorRect.left,
                    top: tableRect.top - anchorRect.top,
                    height: tableRect.height,
                    tableElement: table,
                    columnIndex: cellIndex,
                });
            });
        });

        setResizeHandles(handles);
    }, [editor, anchorElem]);

    // Update handles on editor changes
    useEffect(() => {
        updateResizeHandles();

        return editor.registerUpdateListener(() => {
            editor.getEditorState().read(() => {
                updateResizeHandles();
            });
        });
    }, [editor, updateResizeHandles]);

    // Also update on scroll/resize
    useEffect(() => {
        const handleUpdate = () => updateResizeHandles();
        window.addEventListener('scroll', handleUpdate, true);
        window.addEventListener('resize', handleUpdate);
        return () => {
            window.removeEventListener('scroll', handleUpdate, true);
            window.removeEventListener('resize', handleUpdate);
        };
    }, [updateResizeHandles]);

    const handleMouseDown = (e, handle) => {
        e.preventDefault();

        const table = handle.tableElement;
        const firstRow = table.querySelector('tr');
        const allCells = firstRow.querySelectorAll('th, td');

        // Store a reference to a cell (not the table) - Lexical tracks cells
        const firstCell = allCells[0];

        const allColumnWidths = Array.from(allCells).map(cell => cell.offsetWidth);

        // Set explicit widths on ALL columns immediately
        const rows = table.querySelectorAll('tr');
        rows.forEach(row => {
            const cells = row.querySelectorAll('th, td');
            cells.forEach((cell, idx) => {
                cell.style.width = `${allColumnWidths[idx]}px`;
            });
        });

        dragState.current = {
            startX: e.clientX,
            columnIndex: handle.columnIndex,
            tableElement: table,
            cellElement: firstCell,  // Store cell reference
            allColumnWidths,
        };

        setIsDragging(true);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = useCallback((e) => {
        const { startX, columnIndex, tableElement, allColumnWidths } = dragState.current;
        const deltaX = e.clientX - startX;
        const rows = tableElement.querySelectorAll('tr');

        // Only update the column being dragged
        const newWidth = Math.max(50, allColumnWidths[columnIndex] + deltaX);

        rows.forEach((row) => {
            const cells = row.querySelectorAll('th, td');
            const targetCell = cells[columnIndex];
            if (targetCell) {
                targetCell.style.width = `${newWidth}px`;
            }
        });

        dragState.current.deltaX = deltaX;
        updateResizeHandles();
    }, [updateResizeHandles]);

    const handleMouseUp = useCallback(() => {
        const { columnIndex, deltaX, tableElement, cellElement, allColumnWidths } = dragState.current;

        if (!tableElement || deltaX === undefined) {
            setIsDragging(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            return;
        }

        const finalWidths = allColumnWidths.map((width, idx) => {
            if (idx === columnIndex) {
                return Math.max(50, width + (deltaX || 0));
            }
            return width;
        });

        editor.update(() => {
            // Get the cell node from DOM, then navigate to table
            const cellNode = $getNearestNodeFromDOMNode(cellElement);

            if (!cellNode) {
                console.warn('Could not find cell node');
                return;
            }

            // Navigate: Cell -> Row -> Table
            const rowNode = cellNode.getParent();
            const tableNode = rowNode?.getParent();

            if (!tableNode || !$isTableNode(tableNode)) {
                console.warn('Could not find table node');
                return;
            }

            // Update all cells in the table
            tableNode.getChildren().forEach((row) => {
                if ($isTableRowNode(row)) {
                    const cells = row.getChildren();
                    cells.forEach((cell, idx) => {
                        if ($isTableCellNode(cell) && finalWidths[idx] !== undefined) {
                            cell.setWidth(finalWidths[idx]);
                        }
                    });
                }
            });
        });

        setIsDragging(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    }, [editor, handleMouseMove]);

    return (
        <div className="table-column-resizer-container">
            {resizeHandles.map((handle) => (
                <div
                    key={handle.id}
                    className={`column-resize-handle ${isDragging ? 'dragging' : ''}`}
                    style={{
                        position: 'absolute',
                        left: `${handle.left - 3}px`,
                        top: `${handle.top}px`,
                        width: '6px',
                        height: `${handle.height}px`,
                        cursor: 'col-resize',
                        zIndex: 10,
                    }}
                    onMouseDown={(e) => handleMouseDown(e, handle)}
                />
            ))}
        </div>
    );
};

const TableResizePlugin = ({ anchor }) => {
    return createPortal(<TableResizerContainer anchorElem={anchor} />, anchor);
};

export default TableResizePlugin;